/**
 * Long-running HTTP that survives the page being backgrounded.
 *
 * Axios uses XMLHttpRequest.timeout. Chrome/Safari treat a hidden tab as not
 * fully active and will fire that timeout (aborting the socket) even though
 * wall-clock time has not elapsed. Native fetch + a timeout that is paused
 * while document.hidden keeps the Cloud Run request alive until the tab is
 * focused again.
 */

export async function backgroundSafeFetch(url, options = {}, timeoutMs = 120000) {
    const controller = new AbortController();
    const userSignal = options.signal;

    if (userSignal) {
        if (userSignal.aborted) {
            controller.abort(userSignal.reason);
        } else {
            userSignal.addEventListener(
                "abort",
                () => controller.abort(userSignal.reason),
                { once: true }
            );
        }
    }

    let timerId;
    const clearTimer = () => {
        if (timerId !== undefined) {
            clearTimeout(timerId);
            timerId = undefined;
        }
    };

    const armTimeout = () => {
        clearTimer();
        if (timeoutMs <= 0) {
            return;
        }
        if (typeof document !== "undefined" && document.hidden) {
            return;
        }
        timerId = setTimeout(() => {
            controller.abort();
        }, timeoutMs);
    };

    const onVisibilityChange = () => {
        if (typeof document === "undefined") {
            return;
        }
        if (document.hidden) {
            clearTimer();
        } else {
            armTimeout();
        }
    };

    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibilityChange);
    }

    armTimeout();

    try {
        return await fetch(url, {
            ...options,
            signal: controller.signal,
        });
    } catch (error) {
        if (error?.name === "AbortError") {
            const timeoutError = new Error(`timeout of ${timeoutMs}ms exceeded`);
            timeoutError.name = "TimeoutError";
            throw timeoutError;
        }
        throw error;
    } finally {
        clearTimer();
        if (typeof document !== "undefined") {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        }
    }
}

export async function backgroundSafeJsonPost(url, body, { headers = {}, timeoutMs = 120000 } = {}) {
    const response = await backgroundSafeFetch(
        url,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...headers,
            },
            body: JSON.stringify(body),
        },
        timeoutMs
    );

    const text = await response.text();
    let data = null;
    if (text) {
        try {
            data = JSON.parse(text);
        } catch {
            data = { error: text };
        }
    }

    if (!response.ok) {
        const errorMessage = data?.error || data?.message || response.statusText || "Unknown error";
        throw new Error(errorMessage);
    }

    return data;
}
